<template>
  <div class="wdw-quarter-map-component-root">
    <Loading v-if="isLoading"></Loading>
    <Message v-for="message in messages" :severity="message.severity" :key="message.content">
      {{ message.content }}
    </Message>
    <div class="wdw-quarter-map" aria-hidden="true" role="presentation" v-if="requiredObjectAvailable">
      <div v-if="show_zoom_buttons" class="map-controls">
        <button
          @click="zoomIn"
          class="btn btn-zoom btn-zoom-in panzoom-exclude"
          :class="{ disabled: zoomed_to_max }"
          value="zoom-in"
          role="link"
          tabindex="-1"
        >
          <font-awesome-icon icon="plus" class="fa fa-fw fa-lg" />
        </button>
        <button
          @click="zoomOut"
          class="btn btn-zoom btn-zoom-out panzoom-exclude"
          :class="{ disabled: zoomed_to_min }"
          value="zoom-out"
          role="link"
          tabindex="-1"
        >
          <font-awesome-icon icon="minus" class="fa fa-fw fa-lg" />
        </button>
      </div>
      <div class="pan-zoom">
        <div class="default-map" :class="{ top: showDefaultMap }" v-show="showDefaultMap">
          <inline-svg
            aria-label="Stadtteile von X-Stadt"
            :src="default_map_url"
            @loaded="onDefaultMapLoad()"
          ></inline-svg>
        </div>
        <div class="detailed-map" :class="{ top: !showDefaultMap }" v-show="!showDefaultMap">
          <inline-svg
            v-if="detailed_map_url"
            aria-label="Stadtteile von X-Stadt"
            :src="detailed_map_url"
            @loaded="onDetailedMapLoad()"
          ></inline-svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InlineSvg from 'vue-inline-svg';
import Panzoom from '@panzoom/panzoom';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { projectStore } from '@/store/project-store';
import { domainStore } from '@/store/domain-store';
import { useRoute } from 'vue-router';
import topicRepository from '@/api/topicRepository';
import Loading from '@/components/general/Loading.vue';
import useHashParams from '@/composables/general/useHashParams';
import useTopicTerm from '@/composables/topic/useTopicTerm';
import Message from 'primevue/message';
import projectRepository from '@/api/projectRepository';

export default {
  name: 'wdw-quarter-map',
  components: { InlineSvg, Loading, Message },
  props: {
    domainid: { type: Number, required: true },
    topicid: {
      type: Number,
      required: false,
    },
    projectid: {
      type: Number,
      required: true,
    },
    show_zoom_buttons: { type: Boolean, default: true },
    onclick_base_url: { type: String, default: '' },
  },

  setup(props) {
    //vars
    const route = useRoute();
    const currentTransform = ref(null);
    const panZoomInstance = ref(null);
    const panStartDate = ref(null);
    const panCoords = ref(null);
    const isLoading = ref(false);
    const districtAttributes = ref();
    const messages = ref([]);

    const maxZoom = 2.2;
    const minZoom = 1;
    const DefaultMapSelector = '.default-map';
    const DetailedMapSelector = '.detailed-map';
    const SwitchZoomLevel = 1.5;
    const QuarterSeparator = '_';
    const QuarterCountSumId = 'thoughtsum';

    //composables
    const { setHashParams } = useHashParams(route);
    const { topicNotFound } = useTopicTerm();

    //computed
    const topic = computed(() => {
      return projectStore.getFetchedTopic(props.topicid);
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const topicAvailable = computed(() => {
      if (topic.value) {
        return topic.value.id ? true : false;
      }
      return false;
    });
    const projectAvailable = computed(() => {
      if (project.value) {
        return project.value.id ? true : false;
      }
      return false;
    });

    const projectThoughtCount = computed(() => {
      let thoughtSum = 0;
      if (project.value) {
        project.value.topics.forEach(topic => {
          thoughtSum += topic.thoughtcount;
        });
      }
      return thoughtSum;
    });
    const thoughtCount = computed(() => {
      if (topicAvailable.value) {
        return topic.value.thoughtcount;
      } else if (projectAvailable.value) {
        return projectThoughtCount.value;
      } else return null;
    });
    const default_map_url = computed(() => {
      let map_url = '';
      if (topicAvailable.value) {
        if (topic.value.project_config && topic.value.project_config.svg_map) {
          map_url = topic.value.project_config.svg_map.default_url;
        }
        if (!map_url) {
          map_url = topic.value.config.svg_map.default_url;
        }
      }
      if (!map_url && projectAvailable.value) {
        map_url = project.value.config.svg_map.default_url;
      }
      return map_url;
    });
    const detailed_map_url = computed(() => {
      let map_url = '';
      if (topicAvailable.value && topic.value.project_config && topic.value.project_config.svg_map) {
        map_url = topic.value.project_config.svg_map.detailed_url;
      } else if (projectAvailable.value) {
        map_url = project.value.config.svg_map.detailed_url;
      }
      if (!map_url) {
        // return default url as fallback
        map_url = default_map_url.value;
      }
      return map_url;
    });
    const districtAttrFilter = computed(() => {
      if (topicAvailable.value && topic.value.config && topic.value.config.attributes) {
        return topic.value.config.attributes.district;
      } else if (projectAvailable.value) {
        return project.value.config.attributes.district;
      } else return '';
    });
    const districtAttrParam = computed(() => {
      if (topicAvailable.value || projectAvailable.value) {
        return 'attribute' + districtAttrFilter.value.id;
      } else return '';
    });
    const showDefaultMap = computed(() => {
      if (!currentTransform.value) return true;
      return currentTransform.value.scale < SwitchZoomLevel;
    });
    const zoomed_to_max = computed(() => {
      if (!currentTransform.value) return true;
      return currentTransform.value.scale == maxZoom;
    });
    const zoomed_to_min = computed(() => {
      if (!currentTransform.value) return true;
      return currentTransform.value.scale == minZoom;
    });

    const requiredObjectAvailable = computed(() => {
      if (props.topicid) {
        return topicAvailable.value && districtAttrFilter.value;
      } else {
        return projectAvailable.value && districtAttrFilter.value;
      }
    });

    //methods

    /************************
     **    Selection of Quarters    **
     ************************/
    const deselectEverything = () => {
      document.querySelectorAll('.wdw-quarter-map .selected').forEach(node => {
        node.classList.remove('selected');
      });
    };

    const selectQuarterByIds = quarterIds => {
      if (quarterIds === null) {
        return;
      }
      if (!Array.isArray(quarterIds)) {
        quarterIds = [quarterIds];
      }

      quarterIds.forEach(id =>
        document
          .querySelectorAll(".wdw-quarter-map [id*='" + id + "']") // CSS substring attribute selector
          .forEach(element => {
            if (element.id.includes('_')) {
              // Big map part
              const elemIDs = element.id.split('_');
              if (elemIDs.includes(id)) {
                element.classList.add('selected');
              }
            } else if (element.id == id) {
              // Small map part
              element.classList.add('selected');
            }
          })
      );
    };

    /************************
     **    Quarter Count bubbles    **
     ************************/
    const setQuarterCounts = mapClassSelector => {
      document.querySelectorAll(mapClassSelector + ' .wdw-quarter').forEach(quarter => {
        let sum = 0;
        if (quarter.id === QuarterCountSumId && thoughtCount.value) {
          // try to set overall sum, if element available in svg
          sum = thoughtCount.value;
        } else {
          // get ID string for quarter, can be separted list of IDs, seperator is defined in var
          const ids = quarter.id.split(QuarterSeparator);
          if (ids.length === 0) {
            return;
          }
          // sum up over all IDs
          sum = ids.reduce(
            (sum, id) =>
              sum +
              (Object.prototype.hasOwnProperty.call(districtAttributes.value, id) ? districtAttributes.value[id] : 0),
            0
          );
        }

        // replace text content
        const countTextElement = Array.from(quarter.querySelectorAll('.wdw-quarter-count text tspan'))[0];
        if (countTextElement) {
          countTextElement.textContent = sum;
        }
      });
    };

    /************************
     **    Click Events            **
     ************************/
    const onQuarterClick = event => {
      // check if it was long click (indicating panning)
      if (panStartDate.value && Date.now() - panStartDate.value > 200) {
        // check coords diff as well, don't skip long clicks without movement
        if (panCoords.value.x != 0 || panCoords.value.y != 0) {
          return;
        }
      }
      // retrieve attributes from map part and build hash parameter
      const target = event.currentTarget;
      let hashParam = '';
      let ids = [];
      let url_value = '';
      if (target !== null) {
        ids = target.id.split(QuarterSeparator);
        if (districtAttrFilter.value !== null && districtAttrFilter.value.id !== null) {
          url_value = encodeURIComponent(ids.join(';'));
          if (
            props.onclick_base_url !== '' &&
            props.onclick_base_url !== null &&
            props.onclick_base_url !== undefined
          ) {
            hashParam = '#' + districtAttrParam.value + '=' + url_value;

            // If onclick_base_url is defined, clicking a quarter will lead to a new page
            window.location.href = props.onclick_base_url + hashParam;
          } else {
            deselectEverything();
            // select quarters by ID in both maps (default and detailed)
            const params = {};
            params[districtAttrParam.value] = url_value;
            // also needed for ThoughtFilter.vue
            setHashParams(params);
            selectQuarterByIds(ids);
          }
        }
      }

      // T22382: Scroll to Topic- or Project-Widget if it exists in DOM
      const topicWidget = document.getElementsByClassName('wdw-widget-topic');
      if (topicWidget.length > 0) {
        topicWidget[0].scrollIntoView({ behavior: 'smooth' });
      } else {
        const projectWidget = document.getElementsByClassName('wdw-widget-project');
        if (projectWidget.length > 0) {
          projectWidget[0].scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    const zoomOut = () => {
      panZoomInstance.value.zoomOut();
    };
    const zoomIn = () => {
      panZoomInstance.value.zoomIn();
    };

    /************************
     **    Initialization        **
     ************************/

    const initializeClickHandlers = classSelector => {
      document.querySelectorAll(classSelector + ' .wdw-quarter:not(.disabled)').forEach(node => {
        node.addEventListener('click', onQuarterClick);
      });
    };

    const initializePanZoom = () => {
      if (panZoomInstance.value) return;
      const panZoomElement = document.querySelector('.pan-zoom');
      panZoomInstance.value = Panzoom(panZoomElement, {
        maxScale: maxZoom,
        minScale: minZoom,
        step: 0.2,
        excludeClass: 'panzoom-exclude',
        transformOrigin: {
          x: 0.5,
          y: 0.5,
        },
        contain: 'outside',
        panOnlyWhenZoomed: true, // makes sense with contain outside
        // Only call preventDefault(), to pass down click event
        handleStartEvent: event => {
          event.preventDefault();
        },
      });
      panZoomElement.parentElement.addEventListener('wheel', panZoomInstance.value.zoomWithWheel);
      panZoomElement.addEventListener('panzoomchange', event => {
        currentTransform.value = event.detail;
      });
      panZoomElement.addEventListener('panzoomstart', event => {
        // remember pan Start date
        panCoords.value = event.detail;
        panStartDate.value = Date.now();
      });
      panZoomElement.addEventListener('panzoomend', event => {
        // calc panning coords diff
        panCoords.value.x = panCoords.value.x - event.detail.x;
        panCoords.value.y = panCoords.value.y - event.detail.y;
      });
    };

    const getSelectedIds = () => {
      if (districtAttrFilter.value && route.query[districtAttrParam.value]) {
        const attributes = route.query[districtAttrParam.value].split(',');
        return attributes;
      } else return null;
    };
    const addMessage = (severity, content) => {
      messages.value.push({
        severity: severity,
        content: content,
      });
    };
    const onMapLoad = async classSelector => {
      //NOTE: if problems with onMapLoad appear, use nextTick(() => {})
      // await timeout(100);

      //change quarterCounts from the inherited svg-value of '10'
      //to '...' before filling in the fetched counts
      document.querySelectorAll(classSelector + ' .wdw-quarter').forEach(quarter => {
        // replace text content
        const countTextElement = Array.from(quarter.querySelectorAll('.wdw-quarter-count text tspan'))[0];
        if (countTextElement) {
          countTextElement.textContent = '...';
        }
      });

      initializeClickHandlers(classSelector);
      initializePanZoom();
      if (districtAttributes.value) {
        setQuarterCounts(classSelector);
      } else if (!topicAvailable.value && !projectAvailable.value) {
        addMessage('warn', topicNotFound());
      } else {
        isLoading.value = true;
        //get Attributes
        if (props.topicid) {
          topicRepository
            .getAttributeStats(props.topicid, districtAttrFilter.value.id)
            .then(attr => {
              districtAttributes.value = attr;
              setQuarterCounts(classSelector);
            })
            .catch(error => {
              const errorMessage = 'Beim Laden ist ein Fehler aufgetreten, bitte versuchen Sie es erneut';
              if (
                !messages.value.some(message => {
                  return message.content === errorMessage;
                })
              ) {
                addMessage('warn', errorMessage);
                console.log(error);
              }
            })
            .finally(() => {
              isLoading.value = false;
            });
        } else {
          projectRepository
            .getAttributeStats(props.projectid, districtAttrFilter.value.id)
            .then(attr => {
              districtAttributes.value = attr;
              setQuarterCounts(classSelector);
            })
            .catch(error => {
              const errorMessage = 'Beim Laden ist ein Fehler aufgetreten, bitte versuchen Sie es erneut';
              if (
                !messages.value.some(message => {
                  return message.content === errorMessage;
                })
              ) {
                addMessage('warn', errorMessage);
                console.log(error);
              }
            })
            .finally(() => {
              isLoading.value = false;
            });
        }
      }
      const attributes = getSelectedIds();

      if (attributes) {
        deselectEverything();
        selectQuarterByIds(attributes);
      }
    };

    const onDefaultMapLoad = async () => {
      await onMapLoad(DefaultMapSelector);
    };
    const onDetailedMapLoad = async () => {
      await onMapLoad(DetailedMapSelector);
    };

    //lifehooks
    watch(
      () => route.query[districtAttrParam.value],
      (nV, oV) => {
        if (nV !== oV) {
          // Deselect everything first
          deselectEverything();
          const attributes = getSelectedIds();
          if (attributes) {
            selectQuarterByIds(attributes);
          }
        }
      },
      { deep: true }
    );

    onBeforeMount(async () => {
      domainStore.setDomainId(props.domainid);
      if (props.topicid) {
        projectStore.fetchTopic(props.topicid).catch(err => {
          addMessage('error', topicNotFound());
        });
      } else {
        projectStore.setProjectId(props.projectid).catch(err => {
          addMessage('error', 'Projekt wurde nicht gefunden');
        });
      }
    });

    return {
      default_map_url,
      detailed_map_url,
      districtAttrFilter,
      showDefaultMap,
      zoomed_to_max,
      zoomed_to_min,
      onDefaultMapLoad,
      onDetailedMapLoad,
      onMapLoad,
      zoomIn,
      zoomOut,
      topic,
      districtAttributes,
      isLoading,
      messages,
      requiredObjectAvailable,
    };
  },
};
</script>
<style lang="scss">
.wdw-quarter-map {
  position: relative;
  height: 500px;
  overflow: hidden;

  .map-controls {
    position: absolute;
    right: 0px;
    z-index: 10;
    margin-right: 17px;

    .btn-zoom {
      float: right;
      border: none;
      color: var(--wdw-quarter-map-secondary-contrast-color, var(--wdw-secondary-contrast-color));
    }

    .btn-zoom-out,
    .btn-zoom-in {
      background-color: var(--wdw-quarter-map-secondary-color, var(--wdw-secondary-color));
      &:hover {
        background-color: var(--wdw-quarter-map-secondary-hover-color, var(--wdw-secondary-hover-color));
        color: var(--wdw-quarter-map-secondary-contrast-color, var(--wdw-secondary-contrast-color));
      }
      &.disabled {
        background-color: var(--wdw-quarter-map-secondary-hover-color, var(--wdw-secondary-hover-color));
        color: var(--wdw-quarter-map-secondary-contrast-color, var(--wdw-secondary-contrast-color));
      }
    }
  }

  .pan-zoom {
    height: 100%;
  }

  svg {
    width: 100%;
    height: 100%;
  }

  .default-map,
  .detailed-map {
    z-index: 0;
    height: 100%;
    width: 100%;
    display: none;
  }

  .top {
    z-index: 10;
    display: block;
  }

  .wdw-quarter {
    cursor: pointer;
    path {
      fill: var(--wdw-quarter-map-primary-color, var(--wdw-primary-color));
      stroke: var(--wdw-quarter-map-stroke-color, #fff);
      stroke-width: 2px;
    }
    text {
      fill: hsla(0, 0%, 10%, 1);
    }
    &:hover path {
      filter: var(--wdw-quarter-map-hover-filter, brightness(105%));
      fill: var(--wdw-quarter-map-hover-color, var(--wdw-quarter-map-primary-color, var(--wdw-primary-color)));
    }
    &.selected path {
      filter: var(--wdw-quarter-map-selected-filter, brightness(115%));
      fill: var(--wdw-quarter-map-selected-color, var(--wdw-quarter-map-primary-color, var(--wdw-primary-color)));
    }

    &.disabled {
      cursor: move;
      path,
      &:hover path,
      &.selected path {
        fill: var(--wdw-quarter-map-primary-color, var(--wdw-primary-color));
        filter: brightness(85%);
        stroke: grey;
      }
    }
  }

  .wdw-quarter-label {
    rect {
      fill: var(--wdw-quarter-map-label-color, #fff);
      stroke: var(--wdw-quarter-map-label-stroke-color, #000);
    }
    text {
      fill: var(--wdw-quarter-map-label-text-color, #000);
    }
  }

  .wdw-quarter-count {
    circle,
    ellipse {
      fill: var(--wdw-quarter-map-bubbles-color, #fff);
      stroke: var(--wdw-quarter-map-bubbles-stroke-color, #000);
    }
    text {
      fill: var(--wdw-quarter-map-bubbles-text-color, #000);
    }
  }
}
</style>
